<!--
Copyright 2022 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->

<crucible-dialog
  [dialogTitle]="data.scoringModel.id ? 'Edit Scoring Model' : 'Add Scoring Model'"
  [form]="form"
  submitLabel="Save"
  [submitDisabled]="!data.canEdit || !form.valid || !form.dirty"
  [guardUnsavedWork]="true"
  (cancel)="handleEditComplete(false)"
  (submit)="handleEditComplete(true)"
>
@if (data && data.scoringModel) {
  <div crucibleDialogContent [formGroup]="form">
    <div class="add-margin">
      <div class="field-row">
        <mat-form-field class="description-field full-width">
          <mat-label>Scoring Model Description</mat-label>
          <input matInput formControlName="description" />
        </mat-form-field>
        <button mat-icon-button type="button" class="info-button" tabindex="-1"
          matTooltip="A name or description for this scoring model. Shown in the admin scoring-model list and when selecting a model for an evaluation."
          #descTip="matTooltip" (click)="descTip.toggle()">
          <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
        </button>
      </div>
    </div>
    <div class="add-margin">
      <div class="field-row">
        <mat-form-field class="description-field full-width">
          <mat-label>Scoring Model Status</mat-label>
          <mat-select placeholder="Select a Status" formControlName="status">
            @for (status of data.itemStatuses; track status) {
              <mat-option [value]="status">
                {{ status }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
        <button mat-icon-button type="button" class="info-button" tabindex="-1"
          matTooltip="Lifecycle state of this scoring model. Used for filtering in the admin list and to mark whether a model is still in use."
          #statusTip="matTooltip" (click)="statusTip.toggle()">
          <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
        </button>
      </div>
    </div>
    <div class="add-margin">
      <div class="field-row">
        <mat-form-field class="description-field full-width">
          <mat-label>Calculation Equation</mat-label>
          <input matInput formControlName="calculationEquation" />
        </mat-form-field>
        <button mat-icon-button type="button" class="info-button" tabindex="-1"
          matTooltip="Formula that computes the overall score from the weighted category scores. Supports the following placeholders: {count} (number of scoring categories), {minPossible} (weighted minimum possible score), {maxPossible} (weighted maximum possible score), {sum} (weighted actual score), and {average} (average of weighted category scores). May optionally be prefixed with a clamp range in the form 'MAX > equation' or 'MAX > equation > MIN' to cap the result."
          #modelEqTip="matTooltip" (click)="modelEqTip.toggle()">
          <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
        </button>
      </div>
    </div>
    <div>
      <b>Scoring Options</b>
      <div class="scoring-option">
        <div class="field-row">
          <mat-checkbox formControlName="useUserScore">Use Individual User Scoring</mat-checkbox>
          <button mat-icon-button type="button" class="info-button" tabindex="-1"
            matTooltip="When enabled, each participant can submit their own individual score for the evaluation."
            #useUserTip="matTooltip" (click)="useUserTip.toggle()">
            <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
          </button>
        </div>
      </div>
      <div class="scoring-option">
        <div class="field-row">
          <mat-checkbox formControlName="useTeamScore">Use Team Scoring</mat-checkbox>
          <button mat-icon-button type="button" class="info-button" tabindex="-1"
            matTooltip="When enabled, a team can submit a single team-wide score for the evaluation."
            #useTeamTip="matTooltip" (click)="useTeamTip.toggle()">
            <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
          </button>
        </div>
      </div>
      <div class="scoring-option">
        <div class="field-row">
          <mat-checkbox formControlName="useOfficialScore">Use Official Scoring</mat-checkbox>
          <button mat-icon-button type="button" class="info-button" tabindex="-1"
            matTooltip="When enabled, an official score can be submitted for the evaluation by authorized teams or users."
            #useOfficialTip="matTooltip" (click)="useOfficialTip.toggle()">
            <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
          </button>
        </div>
      </div>
      <div class="scoring-option">
        <div class="field-row">
          <mat-checkbox formControlName="useTeamAverageScore">Use Team Average Scoring</mat-checkbox>
          <button mat-icon-button type="button" class="info-button" tabindex="-1"
            matTooltip="When enabled, a team's score is computed as the average of its members' individual user scores. Requires Use Individual User Scoring."
            #useTeamAvgTip="matTooltip" (click)="useTeamAvgTip.toggle()">
            <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
          </button>
        </div>
      </div>
      <div class="scoring-option">
        <div class="field-row">
          <mat-checkbox formControlName="useTypeAverageScore">Use Type Average Scoring</mat-checkbox>
          <button mat-icon-button type="button" class="info-button" tabindex="-1"
            matTooltip="When enabled, a score is computed as the average across all teams of the same team type. Requires Use Team Scoring."
            #useTypeAvgTip="matTooltip" (click)="useTypeAvgTip.toggle()">
            <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
          </button>
        </div>
      </div>
    </div>
    <div class="add-margin">
      <b>Display Options</b>
      <div class="scoring-option">
        <div class="field-row">
          <mat-checkbox formControlName="useSubmit">Use Submit Button for Submissions</mat-checkbox>
          <button mat-icon-button type="button" class="info-button" tabindex="-1"
            matTooltip="When enabled, participants must click Submit to finalize their response for each move. When disabled, selections are saved automatically."
            #useSubmitTip="matTooltip" (click)="useSubmitTip.toggle()">
            <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
          </button>
        </div>
      </div>
      <div class="scoring-option">
        <div class="field-row">
          <mat-checkbox formControlName="hideScoresOnScoreSheet">Hide Option Values On Scoresheet</mat-checkbox>
          <button mat-icon-button type="button" class="info-button" tabindex="-1"
            matTooltip="When enabled, the numeric value of each scoring option is hidden from participants on the scoresheet. The computed scores are still displayed."
            #hideScoresTip="matTooltip" (click)="hideScoresTip.toggle()">
            <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
          </button>
        </div>
      </div>
      <div class="scoring-option">
        <div class="field-row">
          <mat-checkbox formControlName="displayCommentTextBoxes">Display Comments as Textboxes</mat-checkbox>
          <button mat-icon-button type="button" class="info-button" tabindex="-1"
            matTooltip="When enabled, comments on scoring options are shown as always-visible textboxes instead of collapsible indicators."
            #commentsTip="matTooltip" (click)="commentsTip.toggle()">
            <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
          </button>
        </div>
      </div>
      <div class="scoring-option">
        <div class="field-row">
          <mat-checkbox formControlName="displayScoringModelByMoveNumber">Use Different Scoring Categories by Move Number</mat-checkbox>
          <button mat-icon-button type="button" class="info-button" tabindex="-1"
            matTooltip="When enabled, each scoring category can be restricted to a specific range of moves using its First Move to Display and Last Move to Display fields."
            #byMoveTip="matTooltip" (click)="byMoveTip.toggle()">
            <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
          </button>
        </div>
      </div>
      <div class="scoring-option">
        <div class="field-row">
          <mat-checkbox formControlName="showPastSituationDescriptions">Show Past Situation Descriptions on Dashboard</mat-checkbox>
          <button mat-icon-button type="button" class="info-button" tabindex="-1"
            matTooltip="When enabled, the dashboard displays situation descriptions from previous moves in addition to the current one."
            #pastSitTip="matTooltip" (click)="pastSitTip.toggle()">
            <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
          </button>
        </div>
      </div>
      <div class="add-margin"> &nbsp;</div>
      <div class="scoring-option">
        <div class="field-row">
          <mat-form-field class="description-field full-width">
            <mat-label>Right Side Display</mat-label>
            <mat-select placeholder="Select a Right Side Display Option" formControlName="rightSideDisplay">
              @for (item of data.rightSideDisplays; track item) {
                <mat-option [value]="item">
                  {{ item }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button mat-icon-button type="button" class="info-button" tabindex="-1"
            matTooltip="Chooses what appears in the right-hand pane of the dashboard: a Score Summary, an HTML Block, an Embedded URL, the Scoresheet, or nothing."
            #rsdTip="matTooltip" (click)="rsdTip.toggle()">
            <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
          </button>
        </div>
      </div>
      @if (form.controls['rightSideDisplay'].value === 'HtmlBlock') {
        <div class="scoring-option">
          <div class="field-row">
            <mat-label class="gray">Right Side HTML Block</mat-label>
            <button mat-icon-button type="button" class="info-button" tabindex="-1"
              matTooltip="Rich-text content shown in the right-hand pane when Right Side Display is set to HtmlBlock."
              #htmlTip="matTooltip" (click)="htmlTip.toggle()">
              <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
            </button>
          </div>
          <angular-editor [placeholder]="'Enter content here...'" formControlName="rightSideHtmlBlock" [config]="editorConfig"></angular-editor>
        </div>
      }
      @if (form.controls['rightSideDisplay'].value === 'EmbeddedUrl') {
        <div class="scoring-option">
          <div class="field-row">
            <mat-form-field class="description-field full-width">
              <mat-label>Right Side URL</mat-label>
              <input matInput formControlName="rightSideEmbeddedUrl" />
            </mat-form-field>
            <button mat-icon-button type="button" class="info-button" tabindex="-1"
              matTooltip="URL to embed in the right-hand pane when Right Side Display is set to EmbeddedUrl. The URL must allow being framed."
              #urlTip="matTooltip" (click)="urlTip.toggle()">
              <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
            </button>
          </div>
        </div>
      }
    </div>
  </div>
}
</crucible-dialog>
